import React, { useState, useEffect, useRef, useMemo } from "react";
import JoditEditor from 'jodit-react';
import {
    Home, Save, X, Loader2, Download, FilePlus, Copy, PenTool, Mic, Sparkles, Printer
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { saveAs } from 'file-saver';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { marked } from "marked";

interface DocumentEditorProps {
    fileUrl?: string;
    fileName?: string;
    onClose?: () => void;
    onSave?: (name: string, content: string) => Promise<void>;
    onCopy?: (name: string, content: string) => Promise<void>;
    initialContent?: string;
}

export const DocumentEditor = ({ fileUrl, fileName: initialName, onClose, onSave, onCopy, initialContent }: DocumentEditorProps) => {
    const [fileName, setFileName] = useState(initialName || "Sin Título.docx");
    const [content, setContent] = useState<string>(initialContent ? marked.parse(initialContent) as string : "");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const editorRef = useRef<any>(null);
    const { toast } = useToast();

    useEffect(() => {
        if (fileUrl) {
            const loadContent = async () => {
                setIsLoading(true);
                try {
                    const response = await fetch(fileUrl);
                    if (!response.ok) throw new Error("Network response was not ok");
                    const text = await response.text();

                    // Simple but robust check: if it looks like Markdown markers are present, parse it.
                    const hasMarkdown = /\*\*|###|__|^[*-] |^[0-9]+\. /m.test(text);
                    const finalContent = hasMarkdown ? marked.parse(text) : text;

                    setContent(finalContent as string);
                } catch (e) {
                    console.error("Error loading file:", e);
                    toast({ title: "Error", description: "No se pudo cargar el archivo.", variant: "destructive" });
                    setContent("");
                } finally {
                    setIsLoading(false);
                }
            };
            loadContent();
        }
    }, [fileUrl]);

    // Jodit Config
    const config = useMemo(() => ({
        readonly: false,
        height: '100%',
        width: '100%',
        minHeight: 800,
        enableDragAndDropFileToEditor: true,
        buttons: [
            'source', '|',
            'bold', 'strikethrough', 'underline', 'italic', '|',
            'ul', 'ol', '|',
            'outdent', 'indent', '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'table', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'print', 'about'
        ],
        uploader: {
            insertImageAsBase64URI: true
        },
        removeButtons: [],
        showXPathInStatusbar: false,
        showCharsCounter: false,
        showWordsCounter: false,
        toolbarAdaptive: false,
        statusbar: true,
        language: 'es',
    }), []);

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(fileName, content);
                toast({ title: "Guardado", description: "Documento guardado exitosamente." });
            } catch (e: any) {
                console.error("Error saving:", e);
                toast({ title: "Error", description: "No se pudo guardar.", variant: "destructive" });
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleCopy = async () => {
        if (onCopy) {
            setIsSaving(true);
            try {
                const newName = `Copia de ${fileName}`;
                await onCopy(newName, content);
                setFileName(newName);
                toast({ title: "Copia creada", description: `Se ha creado ${newName}` });
            } catch (e: any) {
                toast({ title: "Error", description: "No se pudo crear copia.", variant: "destructive" });
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleDownloadDocx = () => {
        const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
            "xmlns:w='urn:schemas-microsoft-com:office:word' " +
            "xmlns='http://www.w3.org/TR/REC-html40'>" +
            "<head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title></head><body>";
        const footer = "</body></html>";
        const sourceHTML = header + content + footer;

        const blob = new Blob(['\ufeff', sourceHTML], {
            type: 'application/msword'
        });
        saveAs(blob, fileName.endsWith('.docx') ? fileName : `${fileName}.docx`);
        toast({ title: "Descargado", description: "Archivo Word generado." });
    };

    const handleDownloadHtml = () => {
        const blob = new Blob([content], { type: "text/html;charset=utf-8" });
        saveAs(blob, fileName.endsWith('.html') ? fileName : `${fileName}.html`);
    };

    const handleInsertSignature = () => {
        const signatureHtml = `<br/><br/><div style="border-top: 1px solid #000; width: 250px; padding-top: 10px; margin-top: 30px; font-family: Arial, sans-serif;">
            <p style="margin: 0; font-weight: bold;">Firma Digital</p>
            <p style="margin: 5px 0 0 0; color: #555;">Usuario Verificado</p>
            <p style="margin: 0; font-size: 12px; color: #999;">${new Date().toLocaleString()}</p>
         </div>`;
        setContent(prev => prev + signatureHtml);
        toast({ title: "Firma", description: "Firma insertada al final." });
    };

    return (
        <div className="flex flex-col h-full bg-[#f3f4f6]">
            {/* Top Navigation Bar */}
            <div className="h-10 bg-white border-b flex items-center px-4 text-xs text-blue-600">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 mr-2" onClick={onClose}>
                    <Home className="h-4 w-4 text-gray-400" />
                </Button>
                <span className="text-gray-400 mx-2">/</span>
                <span className="cursor-pointer hover:underline" onClick={onClose}>Archivos</span>
                <span className="text-gray-400 mx-2">/</span>
                <span className="font-semibold text-gray-700">{fileName}</span>
            </div>

            {/* Custom Toolbar (Simulating the 'File' menu etc via Dropdowns) */}
            <div className="bg-white border-b px-4 py-1 flex items-center gap-1">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 text-xs">Archivo</Button></DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem onClick={handleCopy}><Copy className="mr-2 h-4 w-4" /> Hacer una copia</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setContent(""); setFileName("Nuevo.docx"); }}><FilePlus className="mr-2 h-4 w-4" /> Nuevo documento</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleSave}><Save className="mr-2 h-4 w-4" /> Guardar</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Descargar</DropdownMenuLabel>
                        <DropdownMenuItem onClick={handleDownloadDocx}><Download className="mr-2 h-4 w-4" /> Word (.docx)</DropdownMenuItem>
                        <DropdownMenuItem onClick={handleDownloadHtml}><Download className="mr-2 h-4 w-4" /> HTML</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" /> Imprimir / PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 text-xs">Firma electrónica</Button></DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem onClick={handleInsertSignature}><PenTool className="mr-2 h-4 w-4" /> Insertar Firma</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"><Mic className="h-3 w-3" /> Voz</Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-purple-600"><Sparkles className="h-3 w-3" /> Sof-IA</Button>

                <div className="flex-1"></div>

                <Input
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    className="h-7 w-[200px] text-xs border-gray-200 bg-gray-50 mr-2"
                />

                <Button
                    variant="default"
                    size="sm"
                    className="h-7 bg-[#489ba6] hover:bg-[#3d8b96]"
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                    Guardar
                </Button>
            </div>

            {/* Jodit Editor Container */}
            <div className="flex-1 overflow-y-auto relative p-4 flex justify-center bg-gray-100">
                {isLoading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
                    </div>
                )}

                <div className="w-full max-w-[816px] min-h-full shadow-lg bg-white overflow-x-hidden">
                    <JoditEditor
                        ref={editorRef}
                        value={content}
                        config={config}
                        onBlur={(newContent) => setContent(newContent)}
                        onChange={() => { }}
                    />
                </div>
            </div>
        </div>
    )
};
